<template>
    <div class="cost-summary">
        <sidebar-get-in-touch :heading="sidebarsHeading" :description="sidebarsDescription" :emailAddress="sidebarsEmailAddress" :phoneNumber="sidebarsPhoneNumber" class="mb-4"/>

        <div class="mb-5">
            <router-link to="/car-comparison" class="button button--charcoal-ghost button--block">
                &lt; Back to Comparison
            </router-link>
        </div>

        <div>
            <h3>Cost Summary</h3>

            <div v-if="quote && driverProfile">
                <bch-quote-breakdown
                    :quote="quote"
                    :derivative="quote.derivative"
                    :driverProfile="driverProfile"
                    :type="type"
                    :orderSummaryView="true"
                    :adminPortalData="adminPortalData"
                />
            </div>
            <div v-else class="text-center">
                <loading-icon></loading-icon>
                <p>Calculating...</p>
            </div>
        </div>

        <section class="mb-4">
            <h3>Colleague Declarations</h3>
            <h4>I confirm that:</h4>
            <div class="checkbox">
                <input type="checkbox" name="employee-details" id="employee-details" class="employee-details"
                       v-model="confirmations.employeeDetails" disabled>
                <label for="employee-details" class="multiple-lines">These are my colleague details</label>
            </div>

            <div class="checkbox">
                <input type="checkbox" name="vehicle-and-vehicle-options" id="vehicle-and-vehicle-options"
                       class="vehicle-and-vehicle-options" v-model="confirmations.correctVehicle" disabled>
                <label for="vehicle-and-vehicle-options" class="multiple-lines">I have selected the correct
                    vehicle<br/> and vehicle options</label>
            </div>

            <template v-if="currentProduct && confirmations">
                <div v-for="policy in currentProduct.order_stage_three_policies">
                    <template v-if="policy.declaration_required === '1'">
                        <div class="checkbox">
                            <input type="checkbox" name="maintenance-key-points" id="maintenance-key-points"
                                class="maintenance-key-points" v-model="confirmations[stringToKey(policy.title)]" disabled>
                            <label for="maintenance-key-points">I have accepted the {{ policy.title }} points</label>
                        </div>
                    </template>
                </div>
            </template>
        </section>
    </div>
</template>

<script>
import LoadingIcon from "../../loading-icon";
import {EventBus} from '@/event-bus.js';
import SidebarGetInTouch from "../../sidebar-get-in-touch";
import BchQuoteBreakdown from "../../car-quote/bch/quote-breakdown";

export default {
    name: 'bch-cost-summary',
    components: {BchQuoteBreakdown, SidebarGetInTouch, LoadingIcon, EventBus},
    props: {
        confirmations: null,
        driverProfile: null,
        type: null,
        quote: null,
        quoteCosts: null,
        currentProduct: null,
        adminPortalData: null,
    },

    mounted() {
        this.getChargingProfile();
    },

    data() {
        return {
            homeChargingStandardPricePerKWh: 0.15,
            homeChargingStandardPercentage: 25,
            publicChargingStandardPricePerKWh: 0.30,
            publicChargingStandardPercentage: 25,
            publicChargingFastPricePerKWh: 0.65,
            publicChargingFastPercentage: 25,
            publicChargingFreePricePerKWh: 0,
            publicChargingFreePercentage: 25,

            annualMileage: 10000,
            wLTPRangeMiles: 296,
            batterySizeKWh: 93,
        }
    },

    methods: {
        stringToKey(string) {
            return string.replace(/\ /g, '_');
        },
        getChargingProfile() {
              this.$http.get('/api/get-charging-profile')
                .then(response => {
                    this.homeChargingStandardPercentage = response.data.data.home_charging_standard_percentage;
                    this.publicChargingStandardPercentage = response.data.data.public_charging_standard_percentage;
                    this.publicChargingFastPercentage = response.data.data.public_charging_fast_percentage;
                    this.publicChargingFreePercentage = response.data.data.public_charging_free_percentage;
                })
                .catch(err => {
                    EventBus.$emit('loading', false);
                    let response = err.response;

                    this.$notify({
                        group: 'notify',
                        type: 'error',
                        title: "Error",
                        text: response.data.message
                    });

                    this.error = err.message.toString();
                    this.$router.push({ name: response.data.redirect });
                });
        },

        milesPerCalendarMonth() {
            return this.annualMileage / 12;
        },
    },

    computed: {
        loading() {
            if (this.currentProduct.type == 'salsac') {
                return !this.quote || !this.quoteCosts;
            } else {
                return !this.quote || !this.quote.taxCalculations;
            }
        },

        sidebarsDescription() {
            return this.currentProduct ? this.currentProduct.sidebars_description : '';
        },

        sidebarsEmailAddress() {
            return this.currentProduct ? this.currentProduct.sidebars_email_address : '';
        },

        sidebarsHeading() {
            return this.currentProduct ? this.currentProduct.sidebars_heading : '';
        },

        sidebarsPhoneNumber() {
            return this.currentProduct ? this.currentProduct.sidebars_phone_number : '';
        },

        milesPerKWh() {
            return Number(this.wLTPRangeMiles / this.batterySizeKWh).toFixed(2);
        },

        totalMonthlyCost() {
            const milesPerCalendarMonth = this.milesPerCalendarMonth();
            const milesPerKWh = this.wLTPRangeMiles / this.batterySizeKWh

            const homeChargingStandardMonthlyMiles = this.homeChargingStandardPercentage * milesPerCalendarMonth;
            const publicChargingStandardMonthlyMiles = this.publicChargingStandardPercentage * milesPerCalendarMonth
            const publicChargingFastMonthlyMiles = this.publicChargingFastPercentage * milesPerCalendarMonth;
            const publicChargingFreeMonthlyMiles = this.publicChargingFreePercentage * milesPerCalendarMonth;

            const homeChargingStandardTotalKWhForMonthlyMiles = homeChargingStandardMonthlyMiles / milesPerKWh;
            const publicChargingStandardTotalKWhForMonthlyMiles = publicChargingStandardMonthlyMiles / milesPerKWh;
            const publicChargingFastTotalKWhForMonthlyMiles = publicChargingFastMonthlyMiles / milesPerKWh;
            const publicChargingFreeTotalKWhForMonthlyMiles = publicChargingFreeMonthlyMiles / milesPerKWh;
            const homeChargingStandardMonthlyCost = this.homeChargingStandardPricePerKWh * homeChargingStandardTotalKWhForMonthlyMiles;
            const publicChargingStandardMonthlyCost = this.publicChargingStandardPricePerKWh * publicChargingStandardTotalKWhForMonthlyMiles;
            const publicChargingFastMonthlyCost = this.publicChargingFastPricePerKWh * publicChargingFastTotalKWhForMonthlyMiles;
            const publicChargingFreeMonthlyCost = this.publicChargingFreePricePerKWh * publicChargingFreeTotalKWhForMonthlyMiles;
            const totalMonthlyCost = homeChargingStandardMonthlyCost + publicChargingStandardMonthlyCost + publicChargingFastMonthlyCost + publicChargingFreeMonthlyCost;

            return Number(totalMonthlyCost).toFixed(2);
        },

        averagePencePerMile() {
            return Number(this.totalMonthlyCost / this.milesPerCalendarMonth()).toFixed(2);
        },

        totalEstimatedElectricCostPerMonth() {
            const milesPerCalendarMonth = this.milesPerCalendarMonth();
            const ppm = this.totalMonthlyCost / milesPerCalendarMonth;

            return Number((ppm * milesPerCalendarMonth) / 100).toFixed(2);
        },
    }
};
</script>
